// react
import React, { useState, Fragment, useEffect } from "react";
// store
import { routes } from "../../store/routes";
import { addressService } from "../../store/services/ServiceAddress";
import { commonService } from "../../store/services/ServiceCommon";
import { authService } from "../../store/services/ServiceAuth";
import { setSelectCountriesOptions } from "../../store/services/helpers/functions";
import {
    isEmailError,
    isEmpty,
    isEmptyErrorList,
    isPasswordError,
    isPhoneError,
    isPostCodeError,
    isStreetNumberError,
} from "../../store/services/helpers/validation";
// components
import EmailActivationMessage from "./EmailActivationMessage";
import ButtonSubmit from "../../components/buttons/ButtonSubmit";
import RegisterCheckbox from "./RegisterCheckbox";
import RegisterMessageLogin from "./RegisterMessageLogin";
import ModalResponse from "../../components/modals/ModalResponse";
import { Form, Header, Icon } from "semantic-ui-react";
import GoogleSignInButton from "../../components/auth/GoogleSignInButton";
import { Link } from "react-router-dom";
// others
import { GAPageView, initGA } from "../../index";

// component
function RegisterClient({ basketRegistration, setLoginForm, onRegisterStep }) {
    // state
    const [success, setSuccess] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isCheckboxError, setIsCheckboxError] = useState(false);
    const [errorOpen, setErrorOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    // form attributes
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showBillingAddress, setShowBillingAddress] = useState(false);
    const [street, setStreet] = useState("");
    const [number, setNumber] = useState("");
    const [city, setCity] = useState("");
    const [postCode, setPostCode] = useState("");
    const [country, setCountry] = useState("");
    const [countryList, setCountryList] = useState([]);
    const [phone, setPhone] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [consentChecked, setConsentChecked] = useState(false);

    // Google analytics
    useEffect(() => {
        initGA();
        GAPageView();
    }, []);

    // fetch data - country select options
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);

            const result = await commonService.getChoices();
            if (result.status === 200) {
                setCountryList(result.response.countries);
            }

            setIsLoading(false);
        };

        fetchData();
    }, []);

    // set country select options
    const countryOptions = setSelectCountriesOptions(countryList);

    const passwordStrength = (value) => {
        const missing = [];
        if (value.length < 8) missing.push("8 znakov");
        if (!/[A-Z]/.test(value)) missing.push("veľké písmeno");
        if (!/\d/.test(value)) missing.push("číslicu");
        const score = 3 - missing.length;
        let hint = "";
        if (missing.length === 1) hint = `pridaj ${missing[0]}`;
        if (missing.length === 2) hint = `pridaj ${missing[0]} a ${missing[1]}`;
        if (missing.length > 2) hint = `pridaj ${missing.slice(0, -1).join(", ")} a ${missing[missing.length - 1]}`;
        if (score <= 1) return { score, label: "Slabé heslo", hint, color: "#db2828" };
        if (score === 2) return { score, label: "Stredné heslo", hint, color: "#f2711c" };
        return { score, label: "Silné heslo", hint: "", color: "#21ba45" };
    };

    const handleOnChangeCheckbox = (e, data) => {
        setConsentChecked(!!data.checked);
        if (data.checked) {
            setIsCheckboxError(false);
        }
    };

    const handleOnChangeCountry = (e, data) => {
        setCountry(data.value);
    };

    const handleRegister = async (event) => {
        event.preventDefault();
        setIsProcessing(true);

        let formError = false;

        // validate form fields
        const addressTouched = showBillingAddress && (
            !isEmpty(street) || !isEmpty(number) || !isEmpty(city) || !isEmpty(postCode)
        );

        if (
            isEmailError(email) ||
            isPasswordError(password) ||
            (!isEmpty(phone) && isPhoneError(phone)) ||
            (addressTouched && (
                isEmpty(street) ||
                isEmpty(city) ||
                isEmpty(postCode) ||
                (!isEmpty(number) && isStreetNumberError(number)) ||
                isPostCodeError(postCode)
            ))
        ) {
            formError = true;
        }
        if (!document.getElementById("checkbox-register-client").checked) {
            setIsCheckboxError(true);
            formError = true;
        }

        if (!formError) {
            const dataProfile = {
                is_provider: false,
                firstname: firstName,
                lastname: lastName,
                email: email,
                password: password,
            };

            if (!isEmpty(phone)) {
                dataProfile.phone = phone;
            }

            if (addressTouched) {
                const resultAddress = await addressService.createBaseAddress({
                    address_line: "",
                    street: number ? `${street} ${number}` : street,
                    city: city,
                    postcode: postCode,
                    country: country || "SK",
                });

                if (resultAddress.status !== 201) {
                    setErrorMessage("Pri registrácii nastala chyba!");
                    setSuccess(false);
                    setIsProcessing(false);
                    return;
                }

                dataProfile.address = resultAddress.response.id;
            }

            const result = await authService.register(dataProfile);

            if (result.status !== 201) {
                if (result.response.email) {
                    setErrorMessage("Emailová adresa už je obsadená!");
                } else if (result.response.password) {
                    setErrorMessage(
                        "Zadané heslo nie je platné! Zadajte iné heslo."
                    );
                } else {
                    setErrorMessage("Pri registrácii nastala chyba!");
                }

                setErrorOpen(true);
                setSuccess(false);
            } else {
                setSuccess(true);
                if (onRegisterStep) {
                    onRegisterStep("code", email);
                }
            }
            setIsProcessing(false);
        }
        // show form error
        else {
            setSuccess(false);
            setIsProcessing(false);
        }
    };

    const billingAddressOpen = showBillingAddress && (
        !isEmpty(street) || !isEmpty(number) || !isEmpty(city) || !isEmpty(postCode)
    );

    // template
    return (
        <Fragment>
            <style>{`
                .register-client-form-card {
                    padding: 2rem 1.5rem;
                    max-width: 600px;
                    width: 100%;
                    margin: 0 auto;
                    box-sizing: border-box;
                }
                @media (min-width: 768px) {
                    .register-client-form-card {
                        padding: 3rem 2.5rem;
                    }
                }
            `}</style>
                <Fragment>
                    {!success ? (
                        <div className="register-client-form-card" style={basketRegistration ? {
                            background: 'var(--white)',
                            borderRadius: '20px',
                            border: '1px solid #e0e0e0',
                            boxShadow: '0 5px 20px rgba(214, 12, 140, 0.10)',
                            width: '100%',
                            maxWidth: '500px',
                            margin: '0 auto',
                            boxSizing: 'border-box',
                        } : {
                            background: 'var(--white)',
                            borderRadius: '20px',
                            border: '1px solid #e0e0e0',
                            boxShadow: '0 5px 20px rgba(214, 12, 140, 0.10)',
                            position: 'relative',
                            width: '100%',
                            maxWidth: '600px',
                            boxSizing: 'border-box'
                        }}>
                            {!basketRegistration && <div style={{
                                marginBottom: '2rem'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    marginBottom: '1rem'
                                }}>
                                    <Link
                                        to={routes.REGISTER}
                                        style={{
                                            color: 'var(--primary)',
                                            textDecoration: 'none',
                                            fontSize: '0.9rem',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '0.4rem',
                                            fontWeight: '500',
                                            transition: 'opacity 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.opacity = '0.8';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.opacity = '1';
                                        }}
                                    >
                                        <Icon name="arrow left" style={{ margin: 0, fontSize: '0.85rem', position: 'relative', top: '-0.3rem' }} />
                                        <span>zrušiť registráciu</span>
                                    </Link>
                                </div>
                                <Header as="h2" style={{
                                    color: 'var(--dark)',
                                    margin: 0,
                                    fontSize: '1.75rem',
                                    fontWeight: '600',
                                    textAlign: 'center',
                                    lineHeight: '1.3'
                                }}>
                                    Registrácia užívateľského účtu
                                </Header>
                            </div>}
                            {/* basketRegistration && (
                                <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
                                    <img
                                        src="/images/logos/logo_dark_version.png"
                                        width="180"
                                        style={{ display: "block", margin: "0 auto", maxWidth: "100%", height: "auto" }}
                                        alt="Activity Point logo"
                                    />
                                </div>
                            ) */}
                            <Form
                                onSubmit={handleRegister}
                            >
                                <>
                                        <Form.Input
                                            width={16}
                                            size="large"
                                            required
                                            fluid
                                            label="Meno"
                                            placeholder="Meno"
                                            value={firstName}
                                            onChange={(e) => setFirstName(e.target.value)}
                                            style={{ marginBottom: "1.25rem" }}
                                        />
                                        <Form.Input
                                            width={16}
                                            size="large"
                                            required
                                            fluid
                                            label="Priezvisko"
                                            placeholder="Priezvisko"
                                            value={lastName}
                                            onChange={(e) => setLastName(e.target.value)}
                                            style={{ marginBottom: "1.25rem" }}
                                        />
                                        <Form.Input
                                            width={16}
                                            size="large"
                                            required
                                            fluid
                                            label="Email"
                                            placeholder="Email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            style={{ marginBottom: !isEmpty(email) && isEmailError(email) ? "0.35rem" : "1.25rem" }}
                                        />
                                        {!isEmpty(email) && isEmailError(email) && (
                                            <span style={{ display: "block", fontSize: "0.8rem", color: "#db2828", marginBottom: "1.25rem" }}>
                                                Zadaj e-mail v tvare meno@domena.sk
                                            </span>
                                        )}
                                        <Form.Input
                                            width={16}
                                            size="large"
                                            fluid
                                            label="Telefónne číslo (nepovinné)"
                                            placeholder="+421901234567"
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value)}
                                            error={!isEmpty(phone) ? isPhoneError(phone) : false}
                                            style={{ marginBottom: "1.25rem" }}
                                        />
                                        <Form.Input
                                            width={16}
                                            size="large"
                                            required
                                            fluid
                                            type={showPassword ? "text" : "password"}
                                            label="Heslo"
                                            placeholder="Heslo"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            icon={
                                                <Icon
                                                    style={{ cursor: "pointer", opacity: 0.6 }}
                                                    name={showPassword ? "eye slash" : "eye"}
                                                    link
                                                    onClick={() => setShowPassword((prev) => !prev)}
                                                    onMouseDown={(e) => e.preventDefault()}
                                                />
                                            }
                                            style={{ marginBottom: password ? "0.35rem" : "1.25rem" }}
                                        />
                                        {password && (() => {
                                            const strength = passwordStrength(password);
                                            return (
                                                <div style={{ marginBottom: "1.25rem" }}>
                                                    <div style={{ display: "flex", gap: "4px", marginBottom: "0.65rem" }}>
                                                        {[0, 1, 2].map((part) => (
                                                            <div
                                                                key={part}
                                                                style={{
                                                                    flex: 1,
                                                                    height: "4px",
                                                                    borderRadius: "2px",
                                                                    background: part < strength.score ? strength.color : "#eee",
                                                                }}
                                                            />
                                                        ))}
                                                    </div>
                                                    <span style={{ display: "block", fontSize: "0.8rem", color: strength.color, whiteSpace: "nowrap" }}>
                                                        {strength.hint ? `${strength.label} · ${strength.hint}` : strength.label}
                                                    </span>
                                                </div>
                                            );
                                        })()}
                                        <>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowBillingAddress((open) => !open)}
                                                    style={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        gap: "0.4rem",
                                                        background: "none",
                                                        border: "none",
                                                        padding: 0,
                                                        margin: "0.75rem 0 1.75rem",
                                                        color: "var(--primary)",
                                                        fontWeight: 600,
                                                        cursor: "pointer",
                                                        fontSize: "0.95rem",
                                                    }}
                                                >
                                                    <Icon name={showBillingAddress ? "minus" : "plus"} style={{ margin: 0, fontSize: "0.85rem" }} />
                                                    {showBillingAddress ? "Skryť fakturačnú adresu" : "Pridať fakturačnú adresu"}
                                                </button>
                                                {showBillingAddress && (
                                                    <>
                                                        <Form.Input
                                                            width={16}
                                                            size="large"
                                                            fluid
                                                            label="Ulica"
                                                            placeholder="Ulica"
                                                            value={street}
                                                            onChange={(e) => setStreet(e.target.value)}
                                                            error={billingAddressOpen && isEmpty(street) ? { content: "Vyplňte ulicu" } : false}
                                                            style={{ marginBottom: "1.25rem" }}
                                                        />
                                                        <Form.Input
                                                            width={16}
                                                            size="large"
                                                            fluid
                                                            label="Popisné číslo"
                                                            placeholder="Popisné číslo"
                                                            value={number}
                                                            onChange={(e) => setNumber(e.target.value)}
                                                            error={!isEmpty(number) && isStreetNumberError(number) ? isStreetNumberError(number) : false}
                                                            style={{ marginBottom: "1.25rem" }}
                                                        />
                                                        <Form.Input
                                                            width={16}
                                                            size="large"
                                                            fluid
                                                            label="Mesto/Obec"
                                                            placeholder="Mesto/Obec"
                                                            value={city}
                                                            onChange={(e) => setCity(e.target.value)}
                                                            error={billingAddressOpen && isEmpty(city) ? { content: "Vyplňte mesto" } : false}
                                                            style={{ marginBottom: "1.25rem" }}
                                                        />
                                                        <Form.Input
                                                            width={16}
                                                            size="large"
                                                            fluid
                                                            label="PSČ"
                                                            placeholder="PSČ"
                                                            value={postCode}
                                                            onChange={(e) => setPostCode(e.target.value)}
                                                            error={billingAddressOpen && (isEmpty(postCode) || isPostCodeError(postCode)) ? (isPostCodeError(postCode) || { content: "Vyplňte PSČ" }) : false}
                                                            style={{ marginBottom: "1.25rem" }}
                                                        />
                                                        <Form.Select
                                                            size="large"
                                                            fluid
                                                            search
                                                            disabled={isLoading}
                                                            loading={isLoading}
                                                            label="Krajina"
                                                            placeholder={isLoading ? "Načítavam krajiny" : "Vyberte krajinu"}
                                                            options={countryOptions}
                                                            value={country}
                                                            onChange={handleOnChangeCountry}
                                                            style={{ marginBottom: "1.25rem" }}
                                                        />
                                                    </>
                                                )}
                                            </>
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <RegisterCheckbox
                                        checkboxId="checkbox-register-client"
                                        onChange={handleOnChangeCheckbox}
                                        isCheckboxError={isCheckboxError}
                                    />
                                </div>

                                <ButtonSubmit
                                    fluid={true}
                                    loading={isProcessing}
                                    disabled={
                                        isProcessing ||
                                        !isEmptyErrorList([
                                            firstName,
                                            lastName,
                                            email,
                                            password,
                                        ]) ||
                                        isEmailError(email) ||
                                        isPasswordError(password) ||
                                        !consentChecked
                                    }
                                    text="Registrovať"
                                />
                                <div style={{ marginTop: "0.75rem" }}>
                                    <GoogleSignInButton
                                        label="Registrovať cez Google"
                                        nextPath={basketRegistration ? routes.BASKET_PAY : ""}
                                        disabled={!consentChecked}
                                        onBeforeStart={() => {
                                            if (!consentChecked) {
                                                setIsCheckboxError(true);
                                                return false;
                                            }
                                            return true;
                                        }}
                                    />
                                </div>
                                </>
                            </Form>
                            <RegisterMessageLogin onLogin={basketRegistration ? setLoginForm : null} />
                        </div>
                    ) : (
                        <EmailActivationMessage
                            email={email}
                            basketRegistration={basketRegistration}
                            setLoginForm={setLoginForm}
                            onRegisterStep={onRegisterStep}
                        />
                    )}

                    <ModalResponse
                        open={errorOpen}
                        onClose={() => setErrorOpen(false)}
                        icon="warning circle"
                        header="Chyba"
                        message={errorMessage}
                        onClick={() => setErrorOpen(false)}
                    />
                </Fragment>
        </Fragment>
    );
}

export default RegisterClient;
